import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { User,Role,AppConfig } from '../store/slices/authSlice';
import { setAuthData } from '../store/slices/authSlice';
import type { AppDispatch } from '../store/store';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../common/http-auth';
import type {MenuFeatureItem} from '../components/types/types';
import { MenuItems } from 'src/components/menuItems';
interface UseLoginReturn {
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  handleAuthenticationFailure: () => Promise<void>;
}

interface LoginResponse {
  result: number;
  data: {
    user: User;
    tableauToken: string;
    token: string;
    role:Role[];
    appConfigs:AppConfig[];
  };
  messages: string;
}

interface TableauTokenResponse {
  result: number;
  data: {
    tableautoken: string;
  };
  messages: string;
}

export const useLogin = (): UseLoginReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { login: contextLogin } = useAuth();

  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL?.replace(/^"|"$/g, '') ?? '';

const pageRedirection = (menu: MenuFeatureItem[]): void => {
  
  for (const menuItem of MenuItems) {
     const currentMenuItem = menu.find((m) => m.menuKey === menuItem.menuKey);

   
    if (currentMenuItem) {
      
      if (menuItem.hasSubmenu && menuItem.subMenu) {
        
        for (const feature of currentMenuItem.menuFeatures) {
          if (feature.menuFeatureKey !== '' && feature.isSubMenu) {
           
            const subMenu = menuItem.subMenu.find(
              (subItem) => subItem.menuFeatureKey === feature.menuFeatureKey
            );

            if (subMenu) {              
              console.log(`Navigating to submenu link: ${subMenu.link}`);
              navigate(subMenu.link);
              return; 
            }
          }
        }
        
        continue;
      } else {
        // For menu items without submenu, check the feature's menuFeatureKey
        for (const feature of currentMenuItem.menuFeatures) {
          if (feature.menuFeatureKey === '') {
            // If no menuFeatureKey, navigate based on the parent menuKey
            console.log(`Navigating to parent menu link: ${menuItem.link}`);
            navigate(menuItem.link);
            return; // Stop further execution after navigation
          }
        }
      }
    }
  }
};


  const login = useCallback(
    async (username: string, password: string): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${apiBaseUrl}/authentication/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
          throw new Error('Login failed');
        }

        const data = (await response.json()) as LoginResponse;

        if (typeof data.data.tableauToken !== 'string') {
          throw new Error('Invalid response data');
        }
        localStorage.setItem('tableauToken', data.data.tableauToken);
        localStorage.setItem('jwtToken', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        localStorage.setItem('role', JSON.stringify(data.data.role[0]));
        localStorage.setItem('appConfig', JSON.stringify(data.data.appConfigs[0]));
        console.log(data.data.appConfigs[0]);

        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.data.token}`;

        dispatch(
          setAuthData({
            tableauToken: data.data.tableauToken,
            user: data.data.user,
            token: data.data.token,
            role:data.data.role,
            appConfig:data.data.appConfigs
          }),
        );
console.log(data.data.role);
        contextLogin(username);

        if (data.data.tableauToken !== '') {
          const menu = data.data.role[0].menu;
          pageRedirection(menu as MenuFeatureItem[]);
          console.log(menu);
          // navigate('/dashboard');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate, contextLogin, apiBaseUrl],
  );

  const handleAuthenticationFailure = useCallback(
    async (): Promise<void> => {
      const tableauToken = localStorage.getItem('tableauToken') ?? '';
      if (tableauToken === "") {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const payload = JSON.parse(atob(tableauToken.split('.')[1]));
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const exp = payload.exp * 1000; 
      const isTokenExpired = Date.now() >= exp;

      if (isTokenExpired) {
        setIsLoading(true);
        setError(null);

        try {
          const response = await fetch(`${apiBaseUrl}/authentication/tableauauthenticate`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error('Authentication failed');
          }

          const data = (await response.json()) as TableauTokenResponse;

          if (typeof data.data.tableautoken !== 'string') {
            throw new Error('Invalid response data');
          }

          localStorage.setItem('tableauToken', data.data.tableautoken);

          dispatch(
            setAuthData({
              tableauToken: data.data.tableautoken,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              user: JSON.parse(localStorage.getItem('user') ?? '{}'),
              token: localStorage.getItem('jwtToken') ?? '',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              role: JSON.parse(localStorage.getItem('role') ?? '{}'), 
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              appConfig: JSON.parse(localStorage.getItem('appConfig') ?? '{}'), 
            }),
          );
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
          setIsLoading(false);
        }
      }
    },
    [dispatch, apiBaseUrl],
  );

  return { isLoading, error, login, handleAuthenticationFailure };
};
